import React, { createRef, useEffect, useMemo, useReducer, useRef, useState, } from 'react';
import { TPAComponentsProvider, Divider, Button, ButtonPriority, ButtonSize, IconToggle, SectionNotification, StatesButton, StatesButtonStates, } from 'wix-ui-tpa';
import { classes, st } from './EntityAdvancedPermissions.st.css';
import { Button as BO_Button, Text as BO_Text, FloatingNotification, WixDesignSystemProvider, Loader, Divider as BO_Divider, } from '@wix/design-system';
import PermissionsDropdown from './PermissionsDropdown/PermissionsDropdown';
import BackIcon from '../assets/icons/BackIcon.svg';
import ErrorIcon from '../assets/icons/Error.svg';
import ButtonSpinner from '../assets/icons/ButtonSpinner.svg';
import { Error as IconError } from '@wix/wix-ui-icons-common/on-stage';
import { ChevronLeftSmall } from '@wix/wix-ui-icons-common';
import { chunk, groupBy } from 'lodash';
import { advancedPermissionReducer, RESET, SET_ERROR_MESSAGE, SET_PERMISSION_ID, SET_ROLE_TO_PERMISSION, SET_SAVE_STATE, SET_SELECTED_ROLES, STEP2_RESET, } from './EntityAdvancedPermissionsState';
import { CloseReason, DefaultPermissions, Direction, Loading, ResponseStatus, Theme, } from './EntityAdvancedPermissionsTypes';
import he from 'he';
import SearchEntity from './SearchEntity/SearchEntity';
import PermissionsList from './PermissionsList/PermissionsList';
import NotifyBox from './NotifyBox/NotifyBox';
import { Scrollbar } from 'react-scrollbars-custom';
import { asyncForEach } from './Helper/helper';
import HideFolderCheckbox from './HideFolderCheckbox/HideFolderCheckbox';
import { Header } from './Header/Header';
import { STEP_1, STEP_2 } from './common/constants';
function EntityAdvancedPermissions(props) {
    const { removePermissionCode = 'NONE', entityTitle, entityId, className, validateChanges = () => '', labels: { title = 'Manage Permissions', secondTitle = 'Add Permissions', secondSubtitle = 'People added here will be given access to the folder.', cancel, save, add = 'Add', loadingMessage = 'Loading', searchPlaceHolder = 'Add roles & members...', allOptionsSelected = 'You’ve selected all your members.', noOptionsMessage = 'We couldn’t find any matches. Try another search.', backButtonLabel, checkBoxNotifyLabel = 'Notify people', notifyMessagePlaceholder = 'Add A Message (Optional)', notifyMessageLabel = 'Add A Message', notifyLabelHint = 'Notifications will be sent by email and show in the members area.', closeAriaLabel = 'Close', backAriaLabel = 'Back', emptyStateTitle = 'Only Admins Can Access This Folder', emptyStateDescription = 'Add permissions to share this folder with site members, subscription plans and more.', buttonAddNotify = 'Add & Notify', step1SaveError = 'This action didn’t work out, try again later or close the window to continue without saving.', step2SaveError = 'There was a problem saving changes. Please try again later.', rolesListError = 'There was a problem loading permissions. Wait a moment and try again.', numberOfResultsMessage = '{0} results', pressDeleteToRemoveMessage = '{0}: press delete to remove', selectPermissionLabel = 'Select permission', saveSuccessMessage = 'Permissions successfully added', permissionRemovedMessage = 'Permission successfully removed', doneLabel = 'Done', checkboxLabel_hiddenFolders = 'Make this folder hidden so only people with permissions will see it on your site.', learnMore_hiddenFolders = 'Learn more', hideFolderAndSiteVisitorErrorMessage = 'You’ve chosen to hide this folder. To save your changes, remove site visitors from the list.', }, isModal = true, queryFunction, onClose, availablePermissions = DefaultPermissions, onSave, items, direction = Direction.LTR, loading, mobile = false, preloadRoles, theme = Theme.SITE, isSitePublished = 'true', availability, locale = 'en', showHiddenFolder_app, triggerBiEventHiddenCheckboxClick = () => { }, } = props;
    const isDashboard = theme === Theme.DASHBOARD;
    const Provider = isDashboard ? WixDesignSystemProvider : TPAComponentsProvider;
    const searchEntityRef = createRef();
    const [localItems, setLocalItems] = useState(items);
    const [initLocalItems, setInitLocalItems] = useState(false);
    const defaultIsHiddenFolder = availability?.includes('HIDDEN');
    const containerRef = useRef(null);
    useEffect(() => {
        const oldItems = JSON.stringify(items);
        const newItems = JSON.stringify(localItems);
        if (oldItems !== newItems && !initLocalItems) {
            setLocalItems([...items]);
            setInitLocalItems(true);
        }
    }, [items]);
    const step2Permissions = (availablePermissions || []).filter((permission) => !(permission.divider || permission.id === removePermissionCode));
    const [step, setStep] = useState(STEP_1);
    const defaultPermissionId = availablePermissions.length
        ? availablePermissions[0].id
        : '';
    const setFocus = () => {
        if (searchEntityRef.current) {
            searchEntityRef.current.focus();
        }
    };
    useEffect(setFocus, [step]);
    const defaultState = {
        selectedRoles: [],
        roleToPermission: new Map(),
        notify: true,
        notifyMessage: '',
        chosenPermission: defaultPermissionId,
        saveState: StatesButtonStates.IDLE,
        pendingSave: false,
        isHidden: defaultIsHiddenFolder,
        availabilityStatus: {
            itemId: entityId,
            availability: availability ? availability : '',
        },
    };
    const [advancedPermissionState, setAdvancedPermissionState] = useReducer(advancedPermissionReducer, defaultState);
    const closeCallback = () => onClose
        ? onClose({
            closeReason: CloseReason.Cancel,
            availabilityStatus: advancedPermissionState.availabilityStatus,
        })
        : null;
    const backToFirstView = () => {
        setAdvancedPermissionState({
            type: STEP2_RESET,
            selectedRoles: [],
            notify: true,
            notifyMessage: '',
            chosenPermission: defaultPermissionId,
            errorMessage: '',
            availabilityStatus: advancedPermissionState.availabilityStatus,
        });
        setStep(STEP_1);
    };
    const convertMapToArray = (map) => {
        const resultArray = [];
        map.forEach((value, key) => {
            resultArray.push({ id: key, permissionId: value });
        });
        return resultArray;
    };
    const getModalAlertVisible = () => {
        return changeValidationMsg.length > 0 && errorMessageText.length === 0;
    };
    const removeFulfilledChanges = (promisesRes, chunkedPart) => {
        const { roleToPermission } = advancedPermissionState;
        const fulfilledIndices = promisesRes
            .map((res, index) => res.status === 'fulfilled' &&
            res.value?.responseStatus === ResponseStatus.Success
            ? index
            : -1)
            .filter((index) => index !== -1);
        for (const index of fulfilledIndices) {
            const { rolesChanged } = chunkedPart[index];
            rolesChanged.forEach(({ id }) => {
                if (roleToPermission?.has(id)) {
                    roleToPermission?.delete(id);
                }
            });
        }
    };
    const saveClicked = async () => {
        if (advancedPermissionState.saveState === StatesButtonStates.IN_PROGRESS ||
            advancedPermissionState.disableSave === true) {
            return;
        }
        if (!advancedPermissionState.pendingSave) {
            onClose &&
                onClose({
                    closeReason: CloseReason.Cancel,
                    availabilityStatus: advancedPermissionState.availabilityStatus,
                });
            return;
        }
        setAdvancedPermissionState({
            type: SET_SAVE_STATE,
            saveState: StatesButtonStates.IN_PROGRESS,
        });
        setAdvancedPermissionState({
            type: SET_ERROR_MESSAGE,
            errorMessage: '',
        });
        const { roleToPermission, isHidden } = advancedPermissionState;
        const roles = new Map([...(roleToPermission || new Map())]);
        const hide = isHidden !== defaultIsHiddenFolder ? isHidden : undefined;
        const groups = groupBy([...roles.values()], (value) => `${value.notify}-${value.notifyMessage.trim()}`);
        const actionsPayload = [];
        const saveData = {
            entityId,
            rolesChanged: [],
            reloadValue: step === STEP_2,
            modalAlertVisible,
            isHidden: hide,
        };
        if (roles.size === 0) {
            actionsPayload.push(saveData);
        }
        const newSaveData = { ...saveData };
        for (const value of Object.values(groups)) {
            newSaveData.rolesChanged = value.map((val) => {
                return { id: val.roleId, permissionId: val.level };
            });
            if (value[0].notify) {
                newSaveData.notify = value[0].notify;
                newSaveData.notifyMessage = (value[0].notifyMessage || '').trim();
            }
            actionsPayload.push({ ...newSaveData });
        }
        const chunks = chunk(actionsPayload, 5);
        let promisesResult = [];
        await asyncForEach(chunks, async (chunkedPart) => {
            if (onSave) {
                try {
                    const promises = chunkedPart.map((payload) => onSave(payload));
                    const promisesRes = await Promise.allSettled(promises);
                    removeFulfilledChanges(promisesRes, chunkedPart);
                    promisesResult = promisesResult.concat(promisesRes);
                }
                catch (e) {
                    setAdvancedPermissionState({
                        type: SET_SAVE_STATE,
                        saveState: StatesButtonStates.IDLE,
                    });
                }
            }
        });
        const fulfilledAPIs = promisesResult.filter((res) => res.status === 'fulfilled' &&
            res.value?.responseStatus === ResponseStatus.Success);
        const isAllFulfilled = fulfilledAPIs.length === promisesResult.length;
        const availability_status = fulfilledAPIs.length > 0
            ? fulfilledAPIs[fulfilledAPIs.length - 1].value?.availabilityStatus
            : advancedPermissionState.availabilityStatus;
        if (isAllFulfilled) {
            setAdvancedPermissionState({
                type: RESET,
                ...defaultState,
                saveState: StatesButtonStates.SUCCESS,
                availabilityStatus: availability_status,
            });
            if (step === STEP_1) {
                onClose &&
                    onClose({
                        closeReason: CloseReason.SaveSucceed,
                        availabilityStatus: availability_status,
                    });
            }
            else {
                setStep(STEP_1);
            }
        }
        else {
            let currErrorMessage;
            if (step === STEP_1) {
                currErrorMessage = step1SaveError;
            }
            else {
                currErrorMessage = step2SaveError;
            }
            setAdvancedPermissionState({
                type: SET_ERROR_MESSAGE,
                errorMessage: currErrorMessage,
            });
            setAdvancedPermissionState({
                type: SET_SAVE_STATE,
                saveState: StatesButtonStates.IDLE,
                availabilityStatus: availability_status,
            });
        }
    };
    const onBackIconKeyDown = (e) => {
        if (e.key === 'Enter') {
            backToFirstView();
        }
    };
    const setSelectedRoles = (selectedRoles) => {
        if ((selectedRoles === null || selectedRoles.length === 0) &&
            step === STEP_2) {
            backToFirstView();
            return;
        }
        setAdvancedPermissionState({ type: SET_SELECTED_ROLES, selectedRoles });
        const onlyInSelectList = (selectedRoles || []).filter((item) => item.showOnlyInSelectList);
        const roleToPermission = advancedPermissionState.roleToPermission ||
            new Map();
        let roleListChanged = false;
        onlyInSelectList.forEach((role) => {
            if (roleToPermission.has(role.value)) {
                roleListChanged = true;
            }
            roleToPermission.set(role.value, {
                roleId: role.value,
                notify: false,
                notifyMessage: '',
                level: defaultPermissionId || '',
            });
            updateLocalItems({
                id: role.value,
                permissionId: defaultPermissionId || '',
            });
        });
        if (roleListChanged) {
            setAdvancedPermissionState({
                type: SET_ROLE_TO_PERMISSION,
                roleToPermission,
            });
        }
        if (step === STEP_1 && !onlyInSelectList.length) {
            setAdvancedPermissionState({
                type: SET_ERROR_MESSAGE,
                errorMessage: '',
            });
            setStep(STEP_2);
        }
    };
    const handlePreloadRoles = () => {
        if (preloadRoles && preloadRoles.length && queryFunction) {
            const preloadIDs = preloadRoles.join(',');
            setStep(STEP_2);
            queryFunction('', null, preloadIDs)
                .then((res) => {
                if (res?.items?.length) {
                    const preloadItems = res.items;
                    if (preloadItems && preloadItems.length && step === STEP_1) {
                        const updatedRoles = preloadItems.map((i) => {
                            return { ...i, value: i.id, label: i.role };
                        });
                        setSelectedRoles(updatedRoles);
                    }
                }
            })
                .catch((e) => {
                console.error(e);
            });
        }
    };
    useEffect(handlePreloadRoles, [preloadRoles]);
    const permissionIdChanged = (val) => {
        setAdvancedPermissionState({
            type: SET_PERMISSION_ID,
            chosenPermission: val,
        });
    };
    const onRoleToPermissionChange = (val) => {
        const roleToPermission = advancedPermissionState.roleToPermission ||
            new Map();
        const currentPermissionValue = roleToPermission.get(val.id);
        const newVal = currentPermissionValue && val.permissionId !== removePermissionCode
            ? { ...currentPermissionValue, level: val.permissionId }
            : {
                roleId: val.id,
                notify: false,
                notifyMessage: '',
                level: val.permissionId,
            };
        roleToPermission.set(val.id, newVal);
        updateLocalItems(val);
        setAdvancedPermissionState({
            type: SET_ROLE_TO_PERMISSION,
            roleToPermission,
        });
        if (val.permissionId === removePermissionCode) {
            setFocus();
        }
    };
    const addAndNotifyClick = () => {
        const { selectedRoles, chosenPermission, roleToPermission, notify, notifyMessage, } = advancedPermissionState;
        if (selectedRoles) {
            const roleToPermissionMap = roleToPermission || new Map();
            const selectedIds = selectedRoles.map((selectedRole) => selectedRole.id);
            setLocalItems((prevLocalItems) => {
                const updatedLocalItems = prevLocalItems.filter((item) => !selectedIds.includes(item.id));
                selectedRoles.forEach((role) => {
                    const newItem = {
                        ...role,
                        id: role.id || '',
                        role: role.role || '',
                        description: role.description || '',
                        roleType: role.roleType || '',
                        icon: role.icon,
                        selectedPermission: chosenPermission || '',
                        options: DefaultPermissions,
                    };
                    updatedLocalItems.push(newItem);
                    roleToPermissionMap.set(role.id, {
                        roleId: role.id,
                        notify: notify || false,
                        notifyMessage: (notifyMessage || '').trim(),
                        level: chosenPermission || '',
                    });
                });
                return updatedLocalItems;
            });
            if (roleToPermissionMap) {
                setAdvancedPermissionState({
                    type: SET_ROLE_TO_PERMISSION,
                    roleToPermission: roleToPermissionMap,
                });
            }
            backToFirstView();
        }
    };
    const updatedItems = useMemo(() => {
        return localItems.map((item) => {
            const { id } = item;
            const roleToPermission = advancedPermissionState.roleToPermission?.get(id);
            if (roleToPermission !== undefined) {
                return { ...item, selectedPermission: roleToPermission.level };
            }
            return item;
        });
    }, [localItems]);
    const changeValidationMsg = validateChanges(updatedItems);
    const saveButtonText = step === STEP_1
        ? save
        : advancedPermissionState.notify && isSitePublished === 'true'
            ? buttonAddNotify
            : add;
    const errorMessageText = advancedPermissionState.errorMessage ||
        (loading === Loading.Error && step === STEP_1 && rolesListError) ||
        '';
    useEffect(() => {
        const { isHidden } = advancedPermissionState;
        const isSiteVisitorsSelected = updatedItems.filter((item) => item.selectedPermission !== removePermissionCode &&
            item.roleType === 'SITE_VISITORS').length > 0;
        if (isHidden && isSiteVisitorsSelected) {
            setAdvancedPermissionState({
                type: SET_ERROR_MESSAGE,
                errorMessage: hideFolderAndSiteVisitorErrorMessage,
                disableSave: true,
            });
            return;
        }
        setAdvancedPermissionState({
            type: SET_ERROR_MESSAGE,
            errorMessage: '',
            disableSave: false,
        });
    }, [updatedItems, advancedPermissionState.isHidden]);
    const updateLocalItems = (val) => {
        if (step === STEP_1) {
            setLocalItems((prevLocalItems) => {
                return prevLocalItems.map((item) => {
                    if (item.id === val.id) {
                        return { ...item, selectedPermission: val.permissionId };
                    }
                    return item;
                });
            });
        }
    };
    const backButtonSpan = (React.createElement("span", { className: classes.backButtonSpan }, backButtonLabel || 'Back'));
    const modalTitle = step === STEP_1 ? entityTitle : secondTitle;
    const modalSubTitle = step === STEP_1 ? title : secondSubtitle;
    const rtl = direction === Direction.RTL;
    const modalAlertVisible = getModalAlertVisible();
    const features = { newColorsBranding: true };
    const providerProps = isDashboard
        ? { features, value: {} }
        : { value: { mobile, rtl } };
    const sectionNotification = isDashboard ? (React.createElement(FloatingNotification, { type: "warning", text: React.createElement(BO_Text, { size: "small", className: st(classes.wsrTooltipContent) }, changeValidationMsg), fullWidth: false, showCloseButton: false, className: st(classes.wsrNotificationMessage) })) : (React.createElement(SectionNotification, { type: "alert", className: st(classes.notificationMessage) },
        React.createElement(SectionNotification.Icon, { icon: React.createElement(ErrorIcon, null) }),
        React.createElement(SectionNotification.Text, { className: st(classes.notificationMessageText) }, changeValidationMsg)));
    const wsrErrorNotification = 
    // @ts-ignore
    errorMessageText.length > 0 ? React.createElement(FloatingNotification, { type: "destructive", text: React.createElement(BO_Text, { size: 'small', className: st(classes.wsrTooltipContent) }, errorMessageText), fullWidth: false, showCloseButton: false, className: step === STEP_1 ? st(classes.wsrNotificationMessage) : st(classes.wsrNotificationErrorMessage) })
        : '';
    const errorSectionNotification = isDashboard ? (step === STEP_1
        ?
            wsrErrorNotification
        : '') : (React.createElement(SectionNotification, { type: "error", className: st(classes.notificationMessage) },
        React.createElement(SectionNotification.Icon, { icon: React.createElement(IconError, null) }),
        React.createElement(SectionNotification.Text, null, errorMessageText)));
    return (
    // @ts-ignore
    (React.createElement(Provider, { ...providerProps },
        React.createElement("div", { ref: containerRef, className: st(classes.root, { mobile, rtl, step, dashboard: isDashboard, app: !isDashboard }, className), role: "dialog", "aria-labelledby": "modalMainTitle", "aria-describedby": "folderName" },
            React.createElement(Header, { modalTitle: modalTitle, modalSubtitle: modalSubTitle, step: step, isDashboard: isDashboard, mobile: mobile, isModal: isModal, closeAriaLabel: closeAriaLabel, closeCallback: closeCallback, backToFirstView: backToFirstView }),
            React.createElement(Scrollbar, { native: true, translateContentSizeYToHolder: true, noScrollX: true, className: st(classes.stepScrollbar, {
                    active: step === STEP_2 && !mobile,
                }) },
                React.createElement("div", { className: st(classes.roleSelectionContainer, {
                        secondView: isDashboard && step === STEP_2,
                    }) },
                    React.createElement(SearchEntity, { searchEntityRef: searchEntityRef, loadingMessage: loadingMessage, placeholder: he.decode(searchPlaceHolder || ''), setSelectedRoles: setSelectedRoles, queryFunction: queryFunction, allOptionsSelected: allOptionsSelected, noOptionsMessage: noOptionsMessage, className: st(classes.searchEntity), value: advancedPermissionState.selectedRoles, filterShowOnlyInSelectList: step === STEP_2, numberOfResultsMessage: numberOfResultsMessage, pressDeleteToRemoveMessage: pressDeleteToRemoveMessage, isDashboard: isDashboard, updatedItems: updatedItems }),
                    step === STEP_2 && (React.createElement(React.Fragment, null,
                        React.createElement("label", { className: "sr-only", id: "globalSelectPermissionId" }, he.decode(selectPermissionLabel)),
                        React.createElement(PermissionsDropdown, { containerRef: containerRef, ariaLabelledBy: 'globalSelectPermissionId', options: step2Permissions, disabled: false, value: advancedPermissionState.chosenPermission, onChange: permissionIdChanged, className: isDashboard
                                ? st(classes.wsrPermissionsMenu)
                                : st(classes.permissionDropdown, { rtl }), isDashboard: isDashboard, isStep2: step === STEP_2 })))),
                React.createElement("div", { className: st(classes.steps), role: "alert" },
                    errorMessageText.length > 0 && errorSectionNotification,
                    step === STEP_1 && (React.createElement(React.Fragment, null,
                        modalAlertVisible && sectionNotification,
                        React.createElement(PermissionsList, { containerRef: containerRef, removePermissionCode: removePermissionCode, items: updatedItems, loading: loading, onChange: onRoleToPermissionChange, className: st(classes.permissionList), emptyStateTitle: emptyStateTitle, emptyStateDescription: emptyStateDescription, permissionRemovedMessage: permissionRemovedMessage, isDashboard: isDashboard, modalAlertVisible: modalAlertVisible }))),
                    step === STEP_2 && (React.createElement(React.Fragment, null,
                        React.createElement(NotifyBox, { checkBoxNotifyLabel: checkBoxNotifyLabel, notifyMessagePlaceholder: notifyMessagePlaceholder, notifyMessageLabel: notifyMessageLabel, value: advancedPermissionState, onChange: setAdvancedPermissionState, notifyLabelHint: notifyLabelHint, isDashboard: isDashboard, isSitePublished: isSitePublished === 'true' }),
                        isDashboard && wsrErrorNotification)))),
            !isDashboard && React.createElement(Divider, { className: st(classes.divider) }),
            isDashboard && step === STEP_1 && React.createElement(BO_Divider, null),
            React.createElement("div", { className: st(classes.footer) },
                step === STEP_2 && (React.createElement("span", { onClick: backToFirstView, tabIndex: 0, className: st(classes.backButton), role: "button", onKeyDown: onBackIconKeyDown, "aria-label": backAriaLabel }, isDashboard ? (React.createElement(BO_Button, { priority: "secondary", size: ButtonSize.small },
                    React.createElement(ChevronLeftSmall, null),
                    backButtonSpan)) : (React.createElement(IconToggle, { label: backButtonSpan, className: st(classes.backToggleIcon), icon: React.createElement(BackIcon, { tabIndex: -1 }) })))),
                step === STEP_1 && (React.createElement(HideFolderCheckbox, { isDashboard: isDashboard, advancedPermissionState: advancedPermissionState, setAdvancedPermissionState: setAdvancedPermissionState, checkboxLabel: checkboxLabel_hiddenFolders, checkboxLearnMore: learnMore_hiddenFolders, locale: locale, triggerBiEventHiddenCheckboxClick: triggerBiEventHiddenCheckboxClick, isExperimentOn: showHiddenFolder_app })),
                React.createElement("div", { className: classes.actionButtons },
                    step === STEP_1 &&
                        (isDashboard ? (React.createElement(BO_Button, { priority: "secondary", "aria-label": advancedPermissionState.saveState === StatesButtonStates.SUCCESS
                                ? saveSuccessMessage
                                : '', className: classes.wsrCancelBtn, size: ButtonSize.small, disabled: false, onClick: closeCallback }, cancel || 'Cancel')) : (React.createElement(Button, { priority: ButtonPriority.basicSecondary, upgrade: true, className: st(classes.buttonCancel, {
                                invisible: !advancedPermissionState.pendingSave,
                            }), onClick: closeCallback, size: ButtonSize.medium }, cancel || 'Cancel'))),
                    isDashboard ? (React.createElement(BO_Button, { "aria-label": advancedPermissionState.saveState === StatesButtonStates.SUCCESS
                            ? saveSuccessMessage
                            : '', size: ButtonSize.small, disabled: advancedPermissionState.saveState ===
                            StatesButtonStates.IN_PROGRESS ||
                            (step === STEP_1 && advancedPermissionState.disableSave), onClick: step === STEP_2 ? addAndNotifyClick : saveClicked }, advancedPermissionState.saveState !==
                        StatesButtonStates.IN_PROGRESS ? (advancedPermissionState.pendingSave ? (saveButtonText || 'Save') : (doneLabel)) : (React.createElement(React.Fragment, null,
                        React.createElement(Loader, { size: "tiny" }))))) : (React.createElement(StatesButton, { idleContent: advancedPermissionState.pendingSave
                            ? saveButtonText || 'Save'
                            : doneLabel, "aria-label": advancedPermissionState.saveState === StatesButtonStates.SUCCESS
                            ? saveSuccessMessage
                            : '', inProgressContent: React.createElement(ButtonSpinner, { className: st(classes.ButtonSpinner) }), className: st(classes.buttonSend, {
                            progress: advancedPermissionState.saveState ===
                                StatesButtonStates.IN_PROGRESS,
                        }), size: ButtonSize.medium, disabled: step === STEP_1 && advancedPermissionState.disableSave, state: advancedPermissionState.saveState || StatesButtonStates.IDLE, onClick: step === STEP_2 ? addAndNotifyClick : saveClicked, onNotificationEnd: () => setAdvancedPermissionState({
                            type: SET_SAVE_STATE,
                            saveState: StatesButtonStates.IDLE,
                        }) }))))))));
}
export default EntityAdvancedPermissions;
//# sourceMappingURL=EntityAdvancedPermissions.js.map